// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { attributesProps } from "@store/Themes/common/props/attributes";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
/* Starter Custom Code - End */

/* Greif Custom Code - Start */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductAdditionalInfos from "@store/Library/Components/ProductShipsFrom";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { connect } from "react-redux";
/* Greif Custom Code - End */

/* Starter Custom Code - Start */
type PartNumber = string | JSX.Element;
const hasValue = (value?: PartNumber) => {
    return value && (typeof value === "string" || (value as JSX.Element).props?.children);
};
/* Starter Custom Code - End */

interface OwnProps {
    productNumber: PartNumber; // Starter Custom Code
    customerProductNumber?: PartNumber; // Starter Custom Code
    manufacturerItem?: PartNumber; // Starter Custom Code
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // Starter Custom Code
    overridedStyles?: ProductPartNumbersStyles;
    // Greif
    showLineNumber?: boolean;
    cartLineNumber?: number | null;
    showAdditionalInfo?: boolean;
    index?: number | null;
    pricing?: ProductPriceDto | null;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        pageType: getCurrentPage(state).type,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    // Greif
    lineNumberLabelText?: TypographyPresentationProps;
    lineNumberValueText?: TypographyPresentationProps;
    lineNumberPipeStyle?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    erpNumberGridItem: attributesProps.item,
    erpNumberLabelText: attributesProps.label,
    erpNumberValueText: attributesProps.value,
    customerNameGridItem: attributesProps.item,
    customerNameLabelText: attributesProps.label,
    customerNameValueText: attributesProps.value,
    manufacturerItemGridItem: attributesProps.item,
    manufacturerItemLabelText: attributesProps.label,
    manufacturerItemValueText: attributesProps.value,
    wrapper: attributesProps.wrapper,
    innerWrapper: attributesProps.innerWrapper,
    lineNumberLabelText: { ...attributesProps.label, weight: "bold" },
    lineNumberValueText: { ...attributesProps.value, weight: "bold" },
    lineNumberPipeStyle: {
        ...attributesProps.value,
        weight: "bold",
        css: css`
            padding: 0 ${pxToRem(8)};
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    // Starter Custom Code
    overridedStyles,
    // Greif
    showLineNumber = false,
    cartLineNumber,
    pageType,
    showAdditionalInfo = false,
    index,
    pricing,
}) => {
    const styles = useExpandProps(productPartNumbersStyles, extendedStyles, overridedStyles); // Starter Custom Code
    const displayManufacturerNumber = showManufacturerItem && hasValue(manufacturerItem); // Starter Custom Code
    const displayCustomerNumber = showCustomerName && hasValue(customerProductNumber); // Starter Custom Code
    // Starter Removed - numberOfItems
    // Starter Removed - mobileGridItemWidth
    // Starter Removed - desktopGridItemWidth
    // Starter Removed - gridItemWidth

    /* Greif Custom Code - Start */
    const isOrderConfrimationPage = pageType === "OrderConfirmationPage";
    const isReviewAndSubmitDetailsPage = pageType === "CheckoutReviewAndSubmitPage";
    const isOrderDetailsPage = pageType === "OrderDetailsPage";
    const isOrderComplaintPage = pageType === "OrderComplaintPage";
    const isOrderChangeRequest = pageType === "OrderChangeRequestPage";
    const isReviewOrderChangeRequest = pageType === "ReviewOrderChangeRequestPage";

    const displayLineNumber = showLineNumber && cartLineNumber;

    const isLineNumberMultiplied =
        isOrderConfrimationPage ||
        isReviewAndSubmitDetailsPage ||
        isOrderDetailsPage ||
        isOrderComplaintPage ||
        isOrderChangeRequest ||
        isReviewOrderChangeRequest;

    const lineToLineNumber = (line: number) => {
        return line * 10;
    };
    const indexToString = index?.toString();
    /* Greif Custom Code - End */

    return (
        <StyledWrapper {...styles.wrapper} as="span" className="ProductNumber" /* Greif Custom Code */>
            {showProductNumber && (
                <StyledWrapper {...styles.innerWrapper} as="span" /* Starter Custom Code */>
                    {/* Greif Custom Code - Start */}
                    {displayLineNumber && (
                        <>
                            {showLabel ? (
                                <Typography {...styles.lineNumberLabelText}>
                                    {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                    {translate("Line")}
                                    {translate(":")}
                                </Typography>
                            ) : (
                                <VisuallyHidden>
                                    <Typography {...styles.lineNumberLabelText}>
                                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                        {translate("Line")}
                                        {translate(":")}
                                    </Typography>
                                </VisuallyHidden>
                            )}
                            <Typography {...styles.lineNumberValueText} data-test-selector="lineNumber">
                                {isLineNumberMultiplied ? cartLineNumber : lineToLineNumber(cartLineNumber)}
                            </Typography>
                            <Typography {...styles.lineNumberPipeStyle} data-test-selector="lineNumberPipe">
                                {translate("|")}
                            </Typography>
                        </>
                    )}

                    {/* Greif Custom Code - End */}
                    {showLabel && (
                        <Typography {...styles.erpNumberLabelText}>
                            {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                            {translate("ITEM #") /* Greif */}
                            {translate(":") /* Starter Custom Code */}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>
                                {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                {translate("ITEM #") /* Greif */}
                                {translate(":") /* Starter Custom Code */}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayCustomerNumber && (
                <StyledWrapper {...styles.innerWrapper} as="span" /* Starter Custom Code */>
                    {showLabel && (
                        <Typography {...styles.customerNameLabelText}>
                            {translate("CUSTOMER #") /* Greif */}
                            {translate(":") /* Starter Custom Code */}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>
                                {translate("CUSTOMER #") /* Greif */}
                                {translate(":") /* Starter Custom Code */}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {customerProductNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {displayManufacturerNumber && (
                <StyledWrapper {...styles.innerWrapper} as="span" /* Starter Custom Code */>
                    {showLabel && (
                        <Typography {...styles.manufacturerItemLabelText}>
                            {translate("MFG #")}
                            {translate(":") /* Starter Custom Code */}
                        </Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>
                                {translate("MFG #")}
                                {translate(":") /* Starter Custom Code */}
                            </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </StyledWrapper>
            )}
            {/* Greif Custom Code - Start */}
            {showAdditionalInfo && <ProductAdditionalInfos pageType={pageType} pricing={pricing} />}
            {/* Greif Custom Code - End */}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductPartNumbers); // Greif
